/**
 * Copying and pasting workflow nodes on the canvas.
 *
 * Select nodes, Ctrl/Cmd+C, Ctrl/Cmd+V. The clipboard carries readable JSON, so
 * the same copy can be pasted back onto a canvas, pasted into a different
 * workflow, or pasted into a text editor and read — which is the second reason
 * this exists: seeing what a chain of nodes actually holds, prompts and all,
 * without opening each one in turn.
 *
 * The payload carries the connections twice, on purpose:
 *
 *   `connections` on each node   readable, immediate neighbours only, branch
 *                                named in the text ("describe (yes)").
 *                                IGNORED on paste, so it can never disagree
 *                                with reality in a way that breaks anything.
 *
 *   `edges` at the end           the real wiring, and what paste rebuilds from.
 *                                It has to exist separately because a condition
 *                                node's branch lives on the LINE (sourceHandle),
 *                                where no node can express it.
 *
 * Loops need no special handling: the node a loop returns to simply has two
 * entries in its `from`.
 */
import type { Edge, Node } from "@xyflow/react";
import {
  collectStepRefs,
  rewriteStepRefs,
  slugifyLabel,
  uniqueNodeId,
} from "./workflow-node-ids";

/** Marks the clipboard text as ours. Anything else is ignored on paste. */
const MARKER = "workflow-nodes";
const FORMAT_VERSION = 1;

/** Where a pasted copy lands relative to the original, in canvas units. */
const PASTE_OFFSET = { x: 40, y: 40 };

interface ClipboardNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: Record<string, unknown>;
  /** Readable only. Never read back on paste. */
  connections: { from: string[]; to: string[] };
}

interface ClipboardEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  label?: string;
}

export interface ClipboardPayload {
  proclaw: typeof MARKER;
  version: number;
  copiedFrom?: string;
  nodes: ClipboardNode[];
  edges: ClipboardEdge[];
}

/** "emit-url" on a plain line, "describe (yes)" on a condition branch. */
function describeEnd(nodeId: string, handle?: string | null): string {
  return handle ? `${nodeId} (${handle})` : nodeId;
}

/**
 * A fresh id and a matching label for a pasted node.
 *
 * Dropping a node from the palette already mirrors a de-duplicated id's number
 * into its label, so two dropped triggers read "Trigger" and "Trigger 2". Paste
 * has to do the same or the copy looks identical to the original on the canvas
 * while quietly carrying a different id.
 *
 * The trailing number is stripped from the label FIRST, so copying "Normalize
 * Lead 2" gives "Normalize Lead 3" rather than "Normalize Lead 2 2".
 */
function freshIdAndLabel(
  label: unknown,
  type: string,
  taken: Set<string>,
): { id: string; label?: string } {
  const text = typeof label === "string" ? label.trim() : "";
  // "Normalize Lead 2" → stem "Normalize Lead". A label that is only a number
  // keeps its text, since stripping it would leave nothing to name the node.
  const stem = text.replace(/\s+\d+$/, "").trim() || text;
  const base = slugifyLabel(stem, type);
  const id = uniqueNodeId(base, taken);
  if (!text) return { id };
  if (id === base) return { id, label: stem };
  const suffix = id.slice(base.length + 1);
  return { id, label: `${stem} ${suffix}` };
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * Build the clipboard text for the selected nodes.
 *
 * Only edges with BOTH ends inside the selection are written — an edge to a
 * node that was not copied has nothing to attach to on the other side.
 *
 * Returns null when nothing is selected, so the caller can fall through to the
 * browser's own copy behaviour rather than clearing the clipboard.
 */
export function buildClipboardPayload(
  nodes: Node[],
  edges: Edge[],
  selectedIds: Set<string>,
  workflowName?: string,
): string | null {
  const picked = nodes.filter((n) => selectedIds.has(n.id));
  if (picked.length === 0) return null;

  const innerEdges = edges.filter(
    (e) => selectedIds.has(e.source) && selectedIds.has(e.target),
  );

  const payload: ClipboardPayload = {
    proclaw: MARKER,
    version: FORMAT_VERSION,
    ...(workflowName ? { copiedFrom: workflowName } : {}),
    nodes: picked.map((node) => ({
      id: node.id,
      type: (node.type as string) ?? "node",
      position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
      data: (node.data ?? {}) as Record<string, unknown>,
      connections: {
        from: innerEdges
          .filter((e) => e.target === node.id)
          .map((e) => describeEnd(e.source, e.sourceHandle)),
        to: innerEdges
          .filter((e) => e.source === node.id)
          .map((e) => describeEnd(e.target, e.sourceHandle)),
      },
    })),
    edges: innerEdges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      ...(e.sourceHandle ? { sourceHandle: e.sourceHandle } : {}),
      ...(e.targetHandle ? { targetHandle: e.targetHandle } : {}),
      ...(typeof e.label === "string" && e.label ? { label: e.label } : {}),
    })),
  };

  return JSON.stringify(payload, null, 2);
}

// ---------------------------------------------------------------------------
// Paste
// ---------------------------------------------------------------------------

/**
 * Parse clipboard text, returning null for anything that is not ours — a stray
 * paste of ordinary text must never touch the canvas.
 */
export function parseClipboardPayload(text: string): ClipboardPayload | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const candidate = parsed as Partial<ClipboardPayload>;
  if (candidate.proclaw !== MARKER) return null;
  if (!Array.isArray(candidate.nodes) || candidate.nodes.length === 0) return null;
  return {
    proclaw: MARKER,
    version: typeof candidate.version === "number" ? candidate.version : FORMAT_VERSION,
    copiedFrom: typeof candidate.copiedFrom === "string" ? candidate.copiedFrom : undefined,
    nodes: candidate.nodes as ClipboardNode[],
    edges: Array.isArray(candidate.edges) ? (candidate.edges as ClipboardEdge[]) : [],
  };
}

export interface PasteResult {
  nodes: Node[];
  edges: Edge[];
  /**
   * What could not be brought across. Reported to the user rather than
   * swallowed — a paste that half worked has to say so.
   */
  skippedTrigger: boolean;
  danglingRefs: string[];
}

/**
 * Turn a parsed payload into nodes and edges ready to append to the canvas.
 *
 * Every node gets a fresh id, and references BETWEEN pasted nodes are repointed
 * at the copies, so a prompt reading `{{steps.emit-url.url}}` reads from the
 * pasted Emit URL rather than the original. A reference to a node that was not
 * copied cannot be repointed — there is nothing to point at — so it is left
 * alone and reported.
 */
export function preparePaste(
  payload: ClipboardPayload,
  existingNodes: Node[],
  existingEdges: Edge[],
): PasteResult {
  const takenNodeIds = new Set(existingNodes.map((n) => n.id));
  const takenEdgeIds = new Set(existingEdges.map((e) => e.id));
  const hasTrigger = existingNodes.some((n) => n.type === "trigger");

  // A workflow is allowed exactly one trigger, so a copied one is dropped when
  // the target already has its own. Everything else still pastes.
  const incoming = payload.nodes.filter((n) => !(n.type === "trigger" && hasTrigger));
  const skippedTrigger = incoming.length !== payload.nodes.length;

  // Old id → new id, for repointing edges and step references afterwards.
  // Labels are renumbered alongside, so a pasted copy is tellable from its
  // original on the canvas rather than only in its id.
  const idMap = new Map<string, string>();
  const labelMap = new Map<string, string | undefined>();
  for (const node of incoming) {
    const fresh = freshIdAndLabel(
      (node.data ?? {}).label,
      node.type || "node",
      takenNodeIds,
    );
    takenNodeIds.add(fresh.id);
    idMap.set(node.id, fresh.id);
    labelMap.set(node.id, fresh.label);
  }

  const pastedIds = new Set(incoming.map((n) => n.id));
  const dangling = new Set<string>();

  const nodes: Node[] = incoming.map((node) => {
    const newId = idMap.get(node.id) as string;
    let data = (node.data ?? {}) as Record<string, unknown>;

    // Anything this node reads from that did not come with it cannot be
    // repointed. Collect before rewriting so the ids are still the old ones.
    for (const ref of collectStepRefs(data, new Set<string>())) {
      if (!pastedIds.has(ref)) dangling.add(ref);
    }

    for (const [oldId, freshId] of idMap) {
      if (oldId === freshId) continue;
      data = rewriteStepRefs(data, oldId, freshId) as Record<string, unknown>;
    }

    const newLabel = labelMap.get(node.id);
    if (newLabel !== undefined) data = { ...data, label: newLabel };

    return {
      id: newId,
      type: node.type,
      position: {
        x: (node.position?.x ?? 0) + PASTE_OFFSET.x,
        y: (node.position?.y ?? 0) + PASTE_OFFSET.y,
      },
      data,
      selected: true,
    } as Node;
  });

  const edges: Edge[] = [];
  for (const edge of payload.edges) {
    const source = idMap.get(edge.source);
    const target = idMap.get(edge.target);
    // Both ends must have been pasted. A line to a node left behind has
    // nothing to attach to.
    if (!source || !target) continue;
    const base = `paste-${source}-${edge.sourceHandle ?? ""}-${target}`;
    let id = base;
    let n = 2;
    while (takenEdgeIds.has(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    takenEdgeIds.add(id);
    edges.push({
      id,
      source,
      target,
      ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
      ...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}),
      ...(edge.label ? { label: edge.label } : {}),
      animated: true,
    } as Edge);
  }

  return { nodes, edges, skippedTrigger, danglingRefs: [...dangling] };
}
